import type { CaseDetail } from "./api";

const eventNames: Record<string, string> = {
  "InquiryConfirmed.v2": "确认询单并分发",
  "Case.external-draft.v2": "保存对外邮件草稿",
  "Case.progress.v2": "修改案件指标",
  "Case.outcome.v2": "修改案件状态",
  "Case.confirm-history.v2": "确认历史进度",
  "Case.note.v2": "发布对内消息",
  "Case.sourcing-draft.v2": "保存查货单草稿",
  "Case.sourcing-submit.v2": "提交查货单",
  "Case.quote-draft.v2": "保存报价单草稿",
  "Case.quote-submit.v2": "提交报价单",
  "Case.finance.v2": "创建财务单",
  "CaseSourcingDraftSaved.v2": "保存查货单草稿",
  "CaseSourcingDocumentSubmitted.v2": "提交查货单",
  "CaseSourcingDocumentSaved.v3": "保存正式查货单",
  "CaseQuoteDraftSaved.v1": "保存报价单草稿",
  "CaseQuoteDocumentSubmitted.v1": "提交报价单",
  "CaseQuoteDocumentSaved.v2": "保存正式报价单",
  "CaseAutomaticMilestone.v2": "自动更新案件进度",
  "LegacyCaseMapped.v2": "同步历史案件进度",
};
const stageNames: Record<string, string> = {
  INQUIRY_CONFIRMED: "询单确认",
  INITIAL_SOURCING: "首次查货",
  INITIAL_QUOTING: "首次报价",
  SALES_FOLLOW_UP: "销售跟进",
  AWAITING_PAYMENT: "已付款",
  AWAITING_PROCUREMENT: "已采购",
  AWAITING_PO_CONFIRMATION: "待确认 PO",
};
const outcomes: Record<string, string> = {
  PENDING: "进行中",
  WON: "已成单",
  RETURNED: "已退货",
  FAILED: "已失败",
};
const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
const strings = (value: unknown) =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];

export function describeCaseHistory(
  entry: CaseDetail["history"][number],
  metrics: CaseDetail["customMetrics"] = [],
) {
  const before = record(entry.payload.before),
    after = record(entry.payload.after);
  const names = {
    ...stageNames,
    ...Object.fromEntries(metrics.map((item) => [item.key, item.name])),
  };
  const stage = (value: unknown) =>
    typeof value === "string" ? (names[value] ?? "历史自定义指标") : "未设置";
  const changes: string[] = [];
  if (
    typeof after.businessOutcome === "string" &&
    after.businessOutcome !== before.businessOutcome
  ) {
    const target = outcomes[after.businessOutcome] ?? "历史状态";
    changes.push(
      typeof before.businessOutcome === "string"
        ? `案件状态：${outcomes[before.businessOutcome] ?? "历史状态"} → ${target}`
        : `案件状态：${target}`,
    );
  }
  const oldSelections = strings(before.manualProgress),
    newSelections = strings(after.manualProgress);
  if (Array.isArray(after.manualProgress)) {
    const added = newSelections.filter((key) => !oldSelections.includes(key));
    const removed = oldSelections.filter((key) => !newSelections.includes(key));
    if (added.length) changes.push(`勾选指标：${added.map(stage).join("、")}`);
    if (removed.length)
      changes.push(`取消指标：${removed.map(stage).join("、")}`);
  }
  if (
    after.currentProgress !== undefined &&
    after.currentProgress !== before.currentProgress &&
    !changes.some((text) => /^(勾选|取消)指标/.test(text))
  ) {
    changes.push(
      before.currentProgress
        ? `案件进度：${stage(before.currentProgress)} → ${stage(after.currentProgress)}`
        : `案件进度：${stage(after.currentProgress)}`,
    );
  }
  if (
    before.progressReviewRequired === true &&
    after.progressReviewRequired === false
  )
    changes.push("历史进度已确认");
  if (!changes.length) {
    if (entry.eventType === "Case.external-draft.v2")
      changes.push("邮件已保存为草稿，此记录不表示已发送。");
    else if (/DraftSaved|draft/.test(entry.eventType))
      changes.push("单据草稿已保存。");
    else if (/Submitted|submit/.test(entry.eventType))
      changes.push("单据已提交。");
  }
  return { title: eventNames[entry.eventType] ?? "案件操作", changes };
}

export function CaseOperationHistory({ detail }: { detail: CaseDetail }) {
  const entries = [...detail.history].sort(
    (a, b) => Date.parse(b.occurredAt) - Date.parse(a.occurredAt),
  );
  return (
    <section>
      <details>
        <summary>操作记录（{entries.length}）</summary>
        {!entries.length ? (
          <p className="case-history-empty">暂无操作记录</p>
        ) : (
          <ol className="case-history-list" aria-label="案件操作记录">
            {entries.map((entry) => {
              const description = describeCaseHistory(
                entry,
                detail.customMetrics,
              );
              const date = new Date(entry.occurredAt);
              const time = Number.isNaN(date.getTime())
                ? "时间未知"
                : date.toLocaleString("zh-CN", { hour12: false });
              return (
                <li key={entry.id} className="case-history">
                  <strong>{description.title}</strong>
                  <div className="case-history-meta">
                    <span>
                      {entry.actorName ||
                        (entry.payload.actorUserId ? "历史用户" : "系统")}
                      {entry.actorUsername?.trim()
                        ? `（${entry.actorUsername.trim()}）`
                        : ""}
                    </span>
                    <time dateTime={entry.occurredAt}>{time}</time>
                  </div>
                  {description.changes.map((change, index) => (
                    <p key={index}>{change}</p>
                  ))}
                  {entry.payload.reason && (
                    <p className="case-history-reason">
                      操作说明：{entry.payload.reason}
                    </p>
                  )}
                  {entry.payload.evidence && (
                    <p className="case-history-reason">
                      确认依据：{entry.payload.evidence}
                    </p>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </details>
    </section>
  );
}
